import * as api from '@yandex-int/k-common/client/api';

import { fetchWithCsrf } from '@edu-frontend/modules/src/shared/lib/fetchWithPlugins';
import { showNetworkError } from 'platform-components';
import { getTeacherTrainingCoursesConfig } from 'platform-components/src/components/app-factory/selectors';
import { getIsTeacher } from 'platform-components/src/components/user/selectors';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { KPK_JOIN_ERROR_STATUSES, KPK_JOIN_ERROR_IDS } from '../constants';
import { actions } from './actions';
import type { TeacherKPKCourseSettings, TeacherKPKSettings, ExtendedTeacherKPK, GetTeacherKPKResponse } from './types';

export function* teacherKPKsWatcher() {
    yield all([
        takeLatest(actions.getTeacherKPKsRequest, getTeacherKPKsRequestSaga),
        takeLatest(actions.postTeacherKPKsUpdateThreadRequest, postTeacherKPKsUpdateThreadRequestSaga),

        takeLatest(actions.postTeacherKPKJoinRequest, postTeacherKPKJoinSaga),
        takeLatest(actions.postTeacherKPKJoinByCodeRequest, postTeacherKPKJoinByCodeSaga),
    ]);
}

const fetchKPK = (templateIds: TeacherKPKSettings['courseTemplateId'][]) =>
    fetchWithCsrf<GetTeacherKPKResponse>('/api/v5/schoolbook_kpk', {
        method: 'POST',
        body: JSON.stringify([
            {
                type: 'get_kpk_status_request',
                // eslint-disable-next-line camelcase
                template_ids: templateIds,
            },
        ]),
        headers: {
            'Content-Type': 'application/json',
        },
    });

const postUpdateKPKThread = (courseId: ExtendedTeacherKPK['courseId']) =>
    fetchWithCsrf('/api/v5/schoolbook_kpk', {
        method: 'POST',
        body: JSON.stringify([
            {
                type: 'update_course_stream_request',
                // eslint-disable-next-line camelcase
                course_id: courseId,
            },
        ]),
        headers: {
            'Content-Type': 'application/json',
        },
    });

function* getTeacherKPKsRequestSaga() {
    try {
        const { courses } = yield select(getTeacherTrainingCoursesConfig);
        const bunkerKPKs = courses as TeacherKPKSettings[];

        const isTeacher: boolean = yield select(getIsTeacher);

        if (!isTeacher || !bunkerKPKs.length) {
            yield put(actions.getTeacherKPKsSuccess({ extendedKPKs: [], stream: null }));
            return;
        }

        // `yield call` почему-то не возвращает типы
        const { courses: courses2, stream } = yield call(
            fetchKPK,
            bunkerKPKs.map((course) => course.courseTemplateId)
        );
        const backKPKs = courses2 as TeacherKPKCourseSettings[];

        const KPKByTemplateIds: Record<TeacherKPKCourseSettings['template_id'], TeacherKPKCourseSettings> =
            Object.assign({}, ...backKPKs.map((kpk) => ({ [kpk.template_id]: kpk })));

        const extendedKPKs = bunkerKPKs.map((bunkerKPK): ExtendedTeacherKPK => {
            const backKPK = KPKByTemplateIds[bunkerKPK.courseTemplateId];

            if (backKPK) {
                const { id: courseId, status } = backKPK;

                return { ...bunkerKPK, courseId, status };
            }

            return bunkerKPK;
        });

        yield put(actions.getTeacherKPKsSuccess({ extendedKPKs, stream }));
    } catch (error) {
        yield put(actions.getTeacherKPKsError());
        yield put(showNetworkError());
    }
}

function* postTeacherKPKsUpdateThreadRequestSaga(
    action: ReturnType<typeof actions.postTeacherKPKsUpdateThreadRequest>
) {
    try {
        const { onSuccess, courseId } = action.payload;

        yield call(postUpdateKPKThread, courseId);

        if (onSuccess) {
            onSuccess();
        }

        yield put(actions.postTeacherKPKsUpdateThreadSuccess());
    } catch (error) {
        yield put(actions.postTeacherKPKsUpdateThreadError());
        yield put(showNetworkError());
    }
}

function* postTeacherKPKJoinSaga(action: ReturnType<typeof actions.postTeacherKPKJoinRequest>) {
    const { courseTemplateId, onSuccess, onError } = action.payload;

    try {
        if (courseTemplateId === null) {
            throw new Error('Course template id was not provided');
        }

        const data: any[] = yield call(api.postCreateTeacherCoursesByTemplates, { templateIds: [courseTemplateId] });

        const { id: courseId } = data[0];

        yield put(actions.postTeacherKPKJoinSuccess({ courseId, courseTemplateId }));

        if (onSuccess) {
            onSuccess(courseId);
        }
    } catch (error) {
        yield put(actions.postTeacherKPKJoinError());
        yield put(showNetworkError());
        if (onError) {
            onError();
        }
    }
}

function* postTeacherKPKJoinByCodeSaga(action: ReturnType<typeof actions.postTeacherKPKJoinByCodeRequest>) {
    const { code, courseTemplateId, onSuccess } = action.payload;

    try {
        if (courseTemplateId === null) {
            throw new Error('Course template id was not provided');
        }

        yield call(api.postTeacherTrainingCourseCode, { code });

        yield put(actions.postTeacherKPKJoinRequest({ courseTemplateId, onSuccess }));

        yield put(actions.postTeacherKPKJoinByCodeSuccess());
    } catch (error) {
        switch (error.status) {
            case KPK_JOIN_ERROR_STATUSES.CODE_ALREADY_EXISTS:
                yield put(actions.setTeacherKPKJoinByCodeErrorId(KPK_JOIN_ERROR_IDS.CODE_WAS_USED));
                break;
            case KPK_JOIN_ERROR_STATUSES.CODE_IS_INVALID:
                yield put(actions.setTeacherKPKJoinByCodeErrorId(KPK_JOIN_ERROR_IDS.CODE_INVALID));
                break;
            default:
                yield put(actions.setTeacherKPKJoinByCodeErrorId(KPK_JOIN_ERROR_IDS.NETWORK));
                yield showNetworkError(error);
        }

        yield put(actions.postTeacherKPKJoinByCodeError());
    }
}
